import { QueryClient } from '@tanstack/react-query';
import { GetServerSideProps } from 'next';
import { Session } from 'next-auth';
import { getSession } from 'next-auth/react';
import { ReactElement } from 'react';
import styled from 'styled-components';

import { NextPageWithLayout } from '../components/layouts';
import LandingPageLayout from '../components/layouts/LandingPageLayout';
import useRoutePrefetch from '../hooks/useRoutePrefetch';
import APILogger from '../services/APILogger';
import DiscoverService from '../services/DiscoverService/DiscoverService';
import UserTenantsService from '../services/UserTenantsService';
import { isDevelopment } from '../utils/client';
import {
  detectAndBestEffortSetLanguage,
  detectLanguage,
} from '../utils/localisationUtils';
import { dehydrateReactQueryPrefetchedInfiniteQuery } from '../utils/react-query-utils';
import { provisionFlags } from '../utils/ServerSideFeatureFlagUtils';
import UnauthenticatedHome from './UnauthenticatedHome';

const Home: NextPageWithLayout = () => {
  useRoutePrefetch('/[tenantId]/lessons');
  useRoutePrefetch('/[tenantId]/workspace/[workspaceId]');
  useRoutePrefetch('/[tenantId]/discover');

  return (
    <Container>
      <LandingPageLayout hideBlobs={true} disableHeadline={true}>
        <UnauthenticatedHome />
      </LandingPageLayout>
    </Container>
  );
};

const Container = styled.div`
  min-height: 100vh;
`;

const devTemplate = [
  {
    eng: 'iDf6uLx5dImlCHfOHU5S',
    norsk: '8WmL5tuwnoKa11YDqKkg',
  },
  {
    eng: 'Vg6B8aBt3sEApYt4aQlM',
    norsk: 'iDf6uLx5dImlCHfOHU5S',
  },
  {
    eng: 'iDf6uLx5dImlCHfOHU5S',
    norsk: 'Vg6B8aBt3sEApYt4aQlM',
  },
];
const prodTemplate = [
  {
    eng: 'r6mSH0wIKTAsZm3SODRK',
    norsk: 'Y7Xsg8DdQ4SXnClviLkG',
  },
  {
    eng: 'YMOtbdgOv5AMeskXh6mC',
    norsk: 'EXlcvYDjqDjYBfmcKg3D',
  },
  {
    eng: '5YDHNO5H7jI5a2xG9HFC',
    norsk: '6j1z55RI03lX4sLPG9L9',
  },
];

const TIMEOUT = 20000;
export const getServerSideProps: GetServerSideProps = async context => {
  try {
    const sessionPromise = getSession({ req: context.req });
    const timeoutPromise = new Promise((_, reject) =>
      setTimeout(() => reject(new Error('Timeout')), TIMEOUT),
    );

    const session = (await Promise.race([
      sessionPromise,
      timeoutPromise,
    ])) as Session | null;
    const queryClient = new QueryClient();
    const flags = await provisionFlags(context, queryClient, session);

    if (session) {
      const { activeTenantId } = await UserTenantsService.get(session.user.uid);

      return {
        redirect: {
          permanent: false,
          destination: `/${activeTenantId}/home`,
        },
      };
    } else {
      context.res.setHeader(
        'Cache-Control',
        'public, s-maxage=3600, stale-while-revalidate=1209600',
      );
      const logger = new APILogger();
      const queryClient = new QueryClient();
      const lang = detectLanguage(context, logger);

      const template1Index =
        lang === 'nb-NO'
          ? isDevelopment()
            ? devTemplate[0].norsk
            : prodTemplate[0].norsk
          : isDevelopment()
          ? devTemplate[0].eng
          : prodTemplate[0].eng;
      const template2Index =
        lang === 'nb-NO'
          ? isDevelopment()
            ? devTemplate[1].norsk
            : prodTemplate[1].norsk
          : isDevelopment()
          ? devTemplate[1].eng
          : prodTemplate[1].eng;
      const template3Index =
        lang === 'nb-NO'
          ? isDevelopment()
            ? devTemplate[2].norsk
            : prodTemplate[2].norsk
          : isDevelopment()
          ? devTemplate[2].eng
          : prodTemplate[2].eng;

      const p1 = async () =>
        await queryClient.prefetchQuery(
          ['indexed-template', template1Index],
          async () => DiscoverService.findById(template1Index),
          { staleTime: 1000 * 60 * 5 },
        );

      const p2 = async () =>
        await queryClient.prefetchQuery(
          ['indexed-template', template2Index],
          async () => DiscoverService.findById(template2Index),
          { staleTime: 1000 * 60 * 5 },
        );

      const p3 = async () =>
        await queryClient.prefetchQuery(
          ['indexed-template', template3Index],
          async () => DiscoverService.findById(template3Index),
          { staleTime: 1000 * 60 * 5 },
        );

      await Promise.all([p1(), p2(), p3()]);

      return {
        props: {
          ...flags,
          dehydratedState: dehydrateReactQueryPrefetchedInfiniteQuery(queryClient),
          ...(await detectAndBestEffortSetLanguage(context)),
        },
      };
    }
  } catch (error) {
    return {
      props: {},
    };
  }
};

Home.getLayout = function getLayout(page: ReactElement) {
  return <>{page}</>;
};
export default Home;
